import { Typography, TypographyProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';

interface MenuItemTextProps extends Omit<TypographyProps, 'color' | 'textTransform'> {
    children: React.ReactNode;
    /**
     * The context where this menu text is displayed
     * - 'appbar': Top-level AppBar buttons (desktop)
     * - 'dropdown': Dropdown menu items (both mobile and desktop)
     */
    context?: 'appbar' | 'dropdown';
}

/**
 * Reusable component for consistent menu text styling across AppBar and dropdown menus.
 * Ensures all menu text uses proper theme colors and typography instead of hardcoded values.
 * Centralizes ALL menu text styling including color, case, and other typography properties.
 * 
 * @param context - The display context ('appbar' for top-level buttons, 'dropdown' for menu items)
 * @param children - The text content to display
 * @param ...props - Additional Typography props (excluding color and textTransform which are controlled centrally)
 */
const MenuItemText: React.FC<MenuItemTextProps> = ({
    children,
    context = 'appbar',
    sx,
    ...props
}) => {
    const theme = useTheme();

    // Centralized typography styling for all menu items
    const menuTextStyles = {
        color: theme.palette.text.navbar,
        textTransform: 'uppercase' as const, // Consistent uppercase for all menu items
        fontWeight: theme.typography.fontWeightMedium, // Consistent font weight
        fontSize: '0.875rem', // Consistent font size across all menu contexts
    };

    return (
        <Typography
            sx={{
                ...menuTextStyles,
                ...sx, // Allow override if needed, but discourage it
            }}
            {...props}
        >
            {children}
        </Typography>
    );
};

export default MenuItemText;
